import { useCallback, useEffect, useState } from 'react';
import { Box, ButtonBase, InputBase, makeStyles, Popper } from '@material-ui/core';
import _ from 'lodash';
import {
  BED_STATUS_REMARK_COLOR,
  NEW_BED_STATUS_CUBICLE_ITEM_LIST,
  NEW_BED_STATUS_LENGTH,
} from '../../../../comm/constant';
import AutoCompleteInput from '../../../../components/AutoCompleteInput';
import LongRemark from '../../../../components/LongRemark';
import { AddIcon, DropDownIcon, DropUpIcon } from '../../../../components/Icon';
import Dialog from '../../../../components/Dialog';
import { AutoCompleteSelect } from '../../../../components';
import { initColor } from '../../../../utils/newBedStatusUtil';
import useUpdateEffect from '../../../../utils/useUpdateEffect';
import { Checkbox } from '../../../DutyAssign/components/Attendance/Setting';
import StyleButton from '../../../../components/StyleButton';
import ServiceOrderBtn from '../../components/ServiceOrder/ServiceOrderBtn';
import { OrderedCleaningService } from '../../components/ServiceOrder';
import CubicleRemarksPopup from './CubicleRemarksPopup';

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#666666',
  },
  titleRemark: {
    color: '#c0c6cb',
    marginLeft: '0.2rem',
  },
  addRemarkBtn: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#538fff',
  },
  addIcon: {
    fontSize: '1.2rem',
    marginRight: '0.45rem',
  },
  border: {
    width: '0.063rem',
    height: '1.188rem',
    backgroundColor: '#e4e7eb',
  },
  palette: ({ form }) => ({
    width: '1.375rem',
    height: '1.375rem',
    minWidth: '1.375rem',
    minHeight: '1.375rem',
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
    marginRight: '0.75rem',
    backgroundColor: form.remarkColor?.color ? form.remarkColor?.color : 'unsest',
  }),
  dropDownIcon: {
    color: '#538fff',
    fontSize: '0.75rem',
  },
  input: {
    width: '100%',
    minHeight: '6rem',
    padding: '1.25rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '10px',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  remarkInput: {
    width: '100%',
    minHeight: '6rem',
    padding: '1.25rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '10px',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    alignItems: 'flex-start',
  },
  remarkColorPopup: {
    zIndex: '9999',
  },
  colourItem: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    width: '100%',
    height: '2.875rem',
    backgroundColor: '#fff',
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: '1.563rem',
    '&:hover': {
      backgroundColor: '#f5faff',
    },
  },
  star: {
    color: '#ff0000',
  },
  content: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
}));

// view mode 里面 edit cubicle 的 popup
const UpdateCubicle = ({ handleClose, data, handleUpdateCubicle, setPopup, hospCode, wardLocation }) => {
  const [form, setForm] = useState({
    status: data.status ?? '',
    type: data.type ?? '',
    gender: data.gender ?? '',
    color: data.cubColour ?? '',
    remarks: data.remarks ?? '',
    remarkColor: {
      text: data.remarkColor,
      value: data.remarkColor,
      color: BED_STATUS_REMARK_COLOR[data.remarkColor]?.bgColor ?? '#fff',
    },
    borderLine: data.borderLine,
  });
  const classes = useStyles({ form });
  const [remarkAnchorEl, setRemarkAnchorEl] = useState(null);
  const [remarkPopupAnchorEl, setRemarkPopupAnchorEl] = useState(null);

  useEffect(() => {
    setPopup(true);

    return () => setPopup(false);
  }, []);

  const handleRemarkOpen = useCallback(
    (event) => {
      if (remarkAnchorEl) {
        setRemarkAnchorEl(null);
      } else {
        setRemarkAnchorEl(event.currentTarget);
      }
    },
    [remarkAnchorEl]
  );

  const updateForm = useCallback(
    (type) => (e) => {
      switch (type) {
        case 'remarks':
          setForm((o) => ({ ...o, [type]: e.target.value }));
          break;
        case 'type':
        case 'status':
        case 'gender':
        case 'color':
          setForm((o) => ({ ...o, [type]: e }));
          break;
        case 'remarkColor':
          return () => {
            setRemarkAnchorEl(null);
            setForm((o) => ({ ...o, [type]: e }));
          };
        default:
          break;
      }
    },
    []
  );

  // 选择性别的时候，自动切换默认颜色
  useUpdateEffect(() => {
    setForm((o) => ({
      ...o,
      color: initColor(o.gender),
    }));
  }, [form.gender]);

  const handleSubmit = useCallback(() => {
    handleUpdateCubicle(data, form, handleClose);
  }, [form, data, handleClose, handleUpdateCubicle]);

  // remark popup 开关
  const handleRemarkPopup = useCallback(
    (event) => {
      setRemarkPopupAnchorEl(remarkPopupAnchorEl ? null : event.currentTarget);
    },
    [remarkPopupAnchorEl]
  );

  // 关闭remark popup
  const handleCloseRemarkPopup = useCallback(() => {
    setRemarkPopupAnchorEl(null);
  }, []);

  // 选择remark
  const handleSelectRemark = useCallback(
    (remark, color) => () => {
      setForm((o) => ({
        ...o,
        remarks: `${String(o.remarks)} ${String(remark)}`.slice(0, 99),
        remarkColor: {
          text: color,
          value: color,
          color: BED_STATUS_REMARK_COLOR[color]?.bgColor,
        },
      }));
      setRemarkPopupAnchorEl(null);
    },
    []
  );

  // 选择是否显示border
  const handleSelectBorderLine = useCallback(() => {
    setForm((o) => ({
      ...o,
      borderLine: o.borderLine === 'show' ? 'hide' : 'show',
    }));
  }, []);

  return (
    <Dialog open={true} title="Cubicle" handleClose={handleClose} haveFoot={false}>
      <Box display="flex" flexDirection="column" gridGap="1.25rem">
        <Box display="flex" gridGap="1.25rem">
          <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
            <Box display="flex">
              <div className={classes.title}>Cubicle Status </div>
              <LongRemark
                content="• You can select the status in the list or just leave it blank.
• If there is no suitable status in the list, kindly input by free text.
"
              />
            </Box>
            <AutoCompleteInput
              width="100%"
              placeholder="Select Status"
              itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleStatus}
              value={form.status}
              freeSolo
              onChange={updateForm('status')}
              maxLength={NEW_BED_STATUS_LENGTH.CUBICLE_STATUS}
            />
          </Box>
          <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
            <Box display="flex">
              <div className={classes.title}>Cubicle Type</div>
              <LongRemark
                content={`• For general cubicles, leave it blank.
• For adding a nurse station or other non-cubicle area, kindly select "Nurse Station/Misc.".
• If there is no suitable type in the list, kindly input by free text`}
              />
            </Box>
            <AutoCompleteInput
              width="100%"
              placeholder="Select Type"
              freeSolo
              itemList={_.filter(
                NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleType,
                (item) => item.value !== 'Nurse Station/Misc.' && item.value !== 'Memo'
              )}
              value={form.type}
              onChange={updateForm('type')}
              maxLength={NEW_BED_STATUS_LENGTH.CUBICLE_TYPE}
            />
          </Box>
        </Box>
        <Box display="flex" gridGap="1.25rem">
          <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
            <div className={classes.title}>
              Gender <span className={classes.star}>*</span>
            </div>
            <AutoCompleteSelect
              width="100%"
              placeholder="Select Gender"
              itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.Gender}
              value={form.gender}
              onChange={updateForm('gender')}
            />
          </Box>
          <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
            <div className={classes.title}>Cubicle Colour </div>
            <AutoCompleteSelect
              type="color"
              width="100%"
              placeholder="Select Colour"
              itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleColor}
              value={form.color}
              onChange={updateForm('color')}
              colorIconBorder="solid 1px #e4e7eb"
            />
            <Box display="flex" alignItems="center" gridGap="0.75rem">
              <Checkbox check={form.borderLine === 'show'} handleChange={handleSelectBorderLine} />
              <div className={classes.content}>Show border line</div>
            </Box>
          </Box>
        </Box>
        <Box display="flex" gridGap="1.25rem">
          <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="100%">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <div className={classes.title}>
                Remarks
                <span className={classes.titleRemark}>(100 words limited) </span>
              </div>
              <Box display="flex" alignItems="center" gridGap="0.969rem">
                <ButtonBase className={classes.addRemarkBtn} onClick={handleRemarkPopup}>
                  <AddIcon className={classes.addIcon} />
                  Add Remarks
                </ButtonBase>
                <div className={classes.border} />
                <ButtonBase onClick={handleRemarkOpen}>
                  <div className={classes.palette} />
                  {remarkAnchorEl ? (
                    <DropUpIcon className={classes.dropDownIcon} />
                  ) : (
                    <DropDownIcon className={classes.dropDownIcon} />
                  )}
                </ButtonBase>
              </Box>
            </Box>
            <InputBase
              className={classes.remarkInput}
              placeholder="Add remarks here"
              inputProps={{ maxLength: NEW_BED_STATUS_LENGTH.CUBICLE_REMARKS }}
              value={form.remarks}
              onChange={updateForm('remarks')}
              multiline
            />
          </Box>
        </Box>
        <OrderedCleaningService
          marginLeft="0"
          marginTop="0.55rem"
          tableId={data?.id}
          tableName="C"
          reminder={`cubicle ${data?.name}`}
        />
        <Box display="flex" gridGap="1.25rem" justifyContent="center" mt="0.55rem">
          <StyleButton text="Cancel" width="12.5rem" onClick={handleClose} />
          <ServiceOrderBtn
            hospCode={hospCode}
            wardLocation={wardLocation}
            version={data?.version}
            tableId={data?.cubicleId}
            tableName="C"
            closePop={handleClose}
            reminder={`cubicle ${data?.name}`}
            defaultRemark={data?.name}
          />
          <StyleButton text="Update" type="fill" backgroundColor="#28d2ab" width="12.5rem" onClick={handleSubmit} />
        </Box>
      </Box>
      {/* remark color popup */}
      <Popper
        className={classes.remarkColorPopup}
        open={Boolean(remarkAnchorEl)}
        anchorEl={remarkAnchorEl}
        placement="bottom-end"
        transition
      >
        <Box
          display="flex"
          flexDirection="column"
          width="10rem"
          border="solid 1px #e4e7eb"
          bgcolor="#fff"
          boxShadow="0 1px 6px 0 rgba(0, 0, 0, 0.12)"
          borderRadius="10px"
          padding="0.5rem 0"
        >
          {NEW_BED_STATUS_CUBICLE_ITEM_LIST.RemarkColor.map((item) => (
            <ButtonBase className={classes.colourItem} key={item.value} onClick={updateForm('remarkColor')(item)}>
              <div className={classes.palette} style={{ backgroundColor: item.color }} /> {item.text}
            </ButtonBase>
          ))}
        </Box>
      </Popper>

      {/* remark popup */}
      <CubicleRemarksPopup
        anchorEl={remarkPopupAnchorEl}
        handleClose={handleCloseRemarkPopup}
        handleClick={handleSelectRemark}
      />
    </Dialog>
  );
};

export default UpdateCubicle;
